import React, { useRef, useState } from 'react';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { GeneratedImage } from '@/types';
import GalleryImageOverlay from './GalleryImageOverlay';
import { resolveImageUrl } from './utils';
import { useTranslation } from '@/i18n/useTranslation';


interface GallerySingleViewProps {
  currentImage: GeneratedImage;
  isDesktop: boolean;
  infoPanelVisible: boolean;
  onDelete: (image: GeneratedImage, e?: React.MouseEvent) => void;
  onRegenerate: (image: GeneratedImage, e?: React.MouseEvent) => void;
  onMobileInfoOpen: () => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
}

export default function GallerySingleView({
  currentImage,
  isDesktop,
  infoPanelVisible,
  onDelete,
  onRegenerate,
  onMobileInfoOpen,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}: GallerySingleViewProps) {
  const { t } = useTranslation();
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, url: string) => {
    const img = e.target as HTMLImageElement;
    if (img.dataset.retried === 'true') {
      setImageLoaded(true); // Show even if error to hide skeleton
      return;
    }
    img.dataset.retried = 'true';
    if (!img.src.startsWith('http')) {
      img.src = resolveImageUrl(url);
    }
  };

  // Reset image loaded state when image changes
  React.useEffect(() => {
    setImageLoaded(false);
  }, [currentImage.id, currentImage.timestamp]);

  return (
    <div
      className="bg-bg-main overflow-hidden flex items-center justify-center w-full h-full"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="w-full max-w-6xl h-full flex items-center justify-center">
        <div className="relative w-full flex items-center justify-center h-[100vh]">
          {/* Image with zoom for all types */}
          <div className="relative w-full h-full" ref={imageContainerRef}>
            <TransformWrapper
              ref={transformRef}
              key={currentImage.id || currentImage.timestamp}
              initialScale={1}
              minScale={0.1}
              maxScale={5}
              limitToBounds
              centerOnInit
              doubleClick={{ mode: 'zoomIn', step: 0.5 }}
              wheel={{
                step: 0.1,
                activationKeys: ['Control', 'Meta'],
                wheelDisabled: false,
                touchPadDisabled: false,
              }}
              panning={{
                velocityDisabled: false,
                allowLeftClickPan: true,
                excluded: ['button', 'a'],
              }}
              pinch={{ disabled: false }}
            >
              {({ zoomIn, zoomOut, resetTransform, instance }) => (
                <>
                  <TransformComponent
                    wrapperClass="!w-full !h-full !overflow-hidden"
                    contentClass="flex items-center justify-center"
                    wrapperStyle={{ width: '100%', height: '100%' }}
                  >
                    {/* Skeleton loader */}
                    {!imageLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-[600px] h-[400px] bg-bg-control rounded animate-pulse" />
                      </div>
                    )}
                    
                    {/* Image */}
                    <img
                      ref={imgRef}
                      src={currentImage.url}
                      alt={currentImage.filename}
                      className={`block rounded shadow-lg select-none transition-opacity duration-300 ${
                        imageLoaded ? 'opacity-100' : 'opacity-0'
                      }`}
                      draggable={false}
                      style={{ 
                        touchAction: 'none',
                        maxWidth: '100%',
                        maxHeight: '80vh',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                      onLoad={() => setImageLoaded(true)}
                      onError={(e) => handleImageError(e, currentImage.url)}
                    />
                  </TransformComponent>

                  {/* Zoom controls - left side */}
                  <div className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-50 pointer-events-none">
                    <button
                      className="w-10 h-10 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg text-white hover:bg-black/80 transition-all flex items-center justify-center pointer-events-auto"
                      title={t('zoom.in')}
                      onClick={() => zoomIn()}
                    >
                      <ZoomIn className="w-5 h-5" strokeWidth={2} />
                    </button>
                    <button
                      className="w-10 h-10 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg text-white hover:bg-black/80 transition-all flex items-center justify-center pointer-events-auto"
                      title={t('zoom.out')}
                      onClick={() => zoomOut()}
                    >
                      <ZoomOut className="w-5 h-5" strokeWidth={2} />
                    </button>
                    <button
                      className="w-10 h-10 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg text-white hover:bg-black/80 transition-all flex items-center justify-center pointer-events-auto"
                      title={t('zoom.reset')}
                      onClick={() => resetTransform()}
                    >
                      <Maximize2 className="w-5 h-5" strokeWidth={2} />
                    </button>
                  </div>
                </>
              )}
            </TransformWrapper>
          </div>
        </div>
      </div>
    </div>
  );
}
